/**
 * Action Drawer Component — Apex Design System.
 *
 * A slide-over panel that opens from the right side of the screen.
 * Used for entity creation, editing, and multi-field actions that
 * would otherwise push page content down as inline expansion panels.
 *
 * Design principles:
 * - Content doesn't shift underneath — drawer overlays on top
 * - Clear visual hierarchy — elevated surface with backdrop dimming
 * - Focused action area — user completes the task and closes
 * - Escape key and backdrop click to dismiss
 * - Three sizes: sm (380px), md (480px), lg (600px)
 *
 * Usage:
 *   <ActionDrawer isOpen={showCreate} onClose={() => setShowCreate(false)}
 *     title="Create Customer" description="Add a new customer to your organization.">
 *     <form>...</form>
 *   </ActionDrawer>
 */

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ActionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  icon?: React.ElementType;
  size?: "sm" | "md" | "lg" | "xl";
  children: React.ReactNode;
}

const sizeClasses = {
  sm: "w-[380px]",
  md: "w-[480px]",
  lg: "w-[600px]",
  xl: "w-[720px]",
};

function ActionDrawer({
  isOpen,
  onClose,
  title,
  description,
  icon: Icon,
  size = "md",
  children,
}: ActionDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Prevent body scroll when open.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-[2px] transition-opacity"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`fixed inset-y-0 right-0 ${sizeClasses[size]} max-w-[calc(100vw-2rem)] flex flex-col bg-[var(--surface-base)] border-l border-[var(--border-default)] shadow-xl`}
        style={{
          animation: "slideInRight 0.25s var(--ease-smooth) forwards",
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-[var(--border-subtle)]">
          <div className="flex items-start gap-3">
            {Icon && (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-interactive-primary)]/10 mt-0.5">
                <Icon className="h-5 w-5 text-[var(--color-interactive-primary)]" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-bold text-[var(--text-primary)]">
                {title}
              </h2>
              {description && (
                <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
                  {description}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export default ActionDrawer;
